import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectAzureToken, 
  selectCurrentToken, 
  selectCompany,
  selectUserNo,
  selectExpires
} from '../../store/slices/authSlice';
import { setAuth } from '../../store/slices/authSlice';
import { RootState } from '..';

// Interface för token-svar från Azure
interface TokenResponse {
  token_type: string;
  scope: string;
  expires_in: number;
  access_token: string;
  refresh_token: string;
}

export function useGraphApi() {
  const dispatch = useDispatch();
  const azureToken = useSelector(selectAzureToken);
  const azureRefreshToken = useSelector((state: RootState) => state.auth.azure_refresh_token);
  const azureTokenExpires = useSelector((state: RootState) => state.auth.azure_token_expires);
  const jwtToken = useSelector(selectCurrentToken);
  const company = useSelector(selectCompany);
  const userNo = useSelector(selectUserNo);
  const expires = useSelector(selectExpires);
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshError, setLastRefreshError] = useState<string | null>(null);

  // Kontrollera om token behöver förnyas (mindre än 5 minuter kvar)
  const needsRefresh = useCallback(() => {
    if (!azureTokenExpires) return true;
    return Date.now() > azureTokenExpires - 5 * 60 * 1000;
  }, [azureTokenExpires]);

  // Funktion för att förnya token
  const refreshToken = useCallback(async () => {
    if (!azureRefreshToken || !jwtToken) {
      throw new Error('No refresh token or JWT available');
    }

    try {
      setIsRefreshing(true);
      setLastRefreshError(null);

      const response = await fetch('/api/v1/Login/RefreshAzureToken', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({ refresh_token: azureRefreshToken })
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const data: TokenResponse = await response.json();
      
      // Uppdatera Redux store och localStorage
      dispatch(setAuth({
        token: jwtToken,
        expires: expires!,
        azure_token: data.access_token,
        azure_refresh_token: data.refresh_token,
        azure_token_expires: Date.now() + (data.expires_in * 1000),
        company: company!,
        userNo: userNo!
      }));

      return data.access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      setLastRefreshError(error instanceof Error ? error.message : 'Unknown refresh error');
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [azureRefreshToken, jwtToken, dispatch, expires, company, userNo]);

  // Funktion för att få en giltig token
  const getValidToken = useCallback(async () => {
    if (!azureToken && !azureRefreshToken) {
      throw new Error('No Azure tokens available');
    }

    if (needsRefresh()) {
      try {
        const newToken = await refreshToken();
        return newToken;
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Returnera befintlig token som fallback om förnyelse misslyckas
        if (azureToken) return azureToken;
        throw error;
      }
    }
    
    return azureToken;
  }, [azureToken, azureRefreshToken, needsRefresh, refreshToken]);

  // Hjälpfunktion för Graph API-anrop
  const callGraphApi = useCallback(async (
    endpoint: string, 
    options: RequestInit = {}
  ) => {
    try {
      const token = await getValidToken();
      
      const response = await fetch(`https://graph.microsoft.com/v1.0${endpoint}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Graph API error: ${response.status} ${JSON.stringify(errorData)}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error calling Graph API:', error);
      throw error;
    }
  }, [getValidToken]);

  // Hjälpfunktion för att ladda ner binärdata (t.ex. bilagor)
  const downloadGraphResource = useCallback(async (
    endpoint: string
  ) => {
    try {
      const token = await getValidToken();
      
      const response = await fetch(`https://graph.microsoft.com/v1.0${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Graph API download error: ${response.status}`);
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Error downloading from Graph API:', error);
      throw error;
    }
  }, [getValidToken]);

  return { 
    callGraphApi, 
    downloadGraphResource,
    isRefreshing,
    lastRefreshError,
    refreshToken
  };
}